import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { MenuCloseReason } from "@angular/material/menu/menu";
import { NgxSpinnerService } from "ngx-spinner";
import {
  MenuAction,
  MenuDivider,
} from "src/app/components/core/eris-menu/eris-menu.component";
import { ErisTableDataSource } from "src/app/models/components/eris-table";
import {
  Audience,
  Transfer,
  TransferFile,
} from "src/app/models/services/audience";
import { AudiencesService } from "src/app/services/audiences/audiences.service";
import { TransfersService } from "src/app/services/audiences/transfers.service";
import { DialogService } from "src/app/services/core/dialog.service";
import { TransferParametersDialogService } from "src/app/services/dialogs/transfer-parameters-dialog.service";
import { TransfersShareDialogService } from "src/app/services/dialogs/transfers-share-dialog.service";
import { UsersService } from "src/app/services/users.service";
import { environment } from "src/environments/environment";
import { timer, Subscription, EMPTY, forkJoin } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { TargetPlatforms } from "src/app/models/services/targetPlatforms.enum";
import { TransfersAttributes } from "src/app/models/services/transfersAttributes.enum";
import { TransfersFilesStatus } from "src/app/models/services/transfersFilesStatus.enum";
import { TransferFileAction } from 'src/app/models/services/transferFileAction.enum';

import { TransferShareAudience, ShareAudience, UpdateSharedAudienceRequest  } from "src/app/models/services/transfer-share-audience.model";

import { TransferShareAudienceService } from "src/app/services/audiences/transfer-share-audience.service";

@Component({
  selector: "transfers-block",
  templateUrl: "./transfers-block.component.html",
  styleUrls: ["./transfers-block.component.sass"],
})
export class TransfersBlockComponent implements OnInit, OnDestroy {

  public readonly TargetPlatforms = TargetPlatforms;
  public readonly TransfersAttributes = TransfersAttributes;
  public readonly TransferFileAction = TransferFileAction;

  @Input()
  transfers: Transfer[] = [];

  @Input()
  audience: Audience;

  @Input()
  filter: string;

  @Input()
  transferCreatedCallback: (audience: Audience) => void;

  @Input()
  onMenuOpened: () => void;

  @Input()
  onMenuClosed: (closeReason: MenuCloseReason) => void;

  @Output()
  onTransferAdded = new EventEmitter<Audience>();

  @Output()
  onTransferChanged = new EventEmitter<Audience>();

  transfersDataSource: ErisTableDataSource;
  public fullTransfers: Transfer[];
  public filesVisibleTransferId?: number;

  private currentPageIndex: number = 1;
  private currentSortField: string = "id";
  private currentSortDirection: string = "DESC";

  menuConfiguration: (MenuAction | MenuDivider)[];
  refreshTimerSubscription: Subscription;
  loadTransfersSubscription: Subscription;
  activePolling: boolean = true;

  public lastShareItems: TransferShareAudience["shareAudience"] = [];
  public lastShareTree: TransferShareAudience["shareTree"] | null = null;

  public menuConfigurationTransfer: MenuAction[] = [
    {
      label: "Send new file",
      iconClass: "pe-7s-next-2",
      action: (context: any) => this.sendNewFile(context),
      checkDisabled: (context: any): boolean => context?.audience.status != 2 || !this.transfersService.isTransferFileActionAllowed(TransferFileAction.CreateAndSend,context)
    },
    {
      label: "Create file (no send)",
      iconClass: "pe-7s-note2",
      action: (context: any) => this.createFile(context),
      checkDisabled: (context: any): boolean => context?.audience.status != 2,
    },
    {
      label: "Update transfer",
      iconClass: "pe-7s-upload",
      action: (context: any) => this.updateTransfer(context),
      checkDisabled: (context: any): boolean => context?.audience.status != 2,
    },
    {
      label: "Delete transfer",
      iconClass: "pe-7s-trash",
      action: (context: any) => this.deleteTransfer(context),
      checkDisabled: (context: any): boolean => context?.audience.status != 2,
    },
    {
      label: "Share transfer",
      iconClass: "pe-7s-share",
      action: (context: any) => this.sharingTransfer(context),
      checkDisabled: (context: any): boolean => !this.transfersService.isTransferFileActionAllowed(TransferFileAction.Share,context),
    },
  ];

  constructor(
    public transfersService: TransfersService,
    public audiencesService: AudiencesService,
    public usersService: UsersService,
    public spinnerService: NgxSpinnerService,
    public transferParametersDialogService: TransferParametersDialogService,
    public transfersShareDialogService: TransfersShareDialogService,
    public dialogService: DialogService,
    private transferShareAudienceService: TransferShareAudienceService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.stopRefreshPolling();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.stopRefreshPolling();
    this.activePolling = true;

    this.transfersDataSource = {
      tableDefinition: !this.audience
        ? [
            { headerDescription: "Target platform", enableSort: true, sortFieldName: "targetPlatformId" },
            { headerDescription: "ID", enableSort: true, sortFieldName: "id" },
            { headerDescription: "Name", enableSort: true, sortFieldName: "name" },
            { headerDescription: "Audience", enableSort: true, sortFieldName: "audience.id" },
            { headerDescription: "Created", enableSort: true, sortFieldName: "created" },
            { headerDescription: "Scheduling", enableSort: true, sortFieldName: "scheduling.name" },
            { headerDescription: "Attributes", enableSort: false },
            { headerDescription: "Identifiers", enableSort: false, headerClass: "text-center" },
            { headerDescription: "", enableSort: false },
            { headerDescription: "", enableSort: false },
          ]
        : [
            { headerDescription: "Target platform", enableSort: true, sortFieldName: "targetPlatformId" },
            { headerDescription: "ID", enableSort: true, sortFieldName: "id" },
            { headerDescription: "Name", enableSort: true, sortFieldName: "name" },
            { headerDescription: "Created", enableSort: true, sortFieldName: "created" },
            { headerDescription: "Scheduling", enableSort: true, sortFieldName: "scheduling.description" },
            { headerDescription: "Attributes", enableSort: false },
            { headerDescription: "Identifiers", enableSort: false, headerClass: "text-center" },
            { headerDescription: "", enableSort: false },
            { headerDescription: "", enableSort: false },
          ],
      data: this.transfers,
      enableSort: true,
      enablePagination: true,
      pageSize: 10,
      serverSideConfiguration: !this.audience
        ? {
            totalCount: 0,
            getDataCallback: (
              pageIndex: number,
              sortField: string,
              sortDirection: string
            ): void => {
              this.currentPageIndex = pageIndex ?? this.currentPageIndex;
              this.currentSortField = sortField ?? this.currentSortField;
              this.currentSortDirection = sortDirection ?? this.currentSortDirection;
              this.loadTransfers(true);
            },
          }
        : undefined,
    };

    if (!!this.audience) {
      this.fullTransfers = this.audience.transfers;
      this.menuConfiguration = [
        {
          label: "Create new transfer",
          iconClass: "fa fa-plus-circle",
          action: () => {
            this.createTransfer();
          },
        },
      ];
    }

    if (!!changes.filter && changes.filter.previousValue != changes.filter.currentValue)
      this.fullTransfers = undefined;

    if (!this.fullTransfers) this.loadTransfers(true);
    else this.startRefreshPolling();
  }

  private loadTransfers = (showSpinner?: boolean): void => {
    console.log("Starting transfers load");
    let _this: TransfersBlockComponent = this;

    if (!this.fullTransfers || !!showSpinner) this.spinnerService.show();

    if (this.audience == null) {
      this.loadTransfersSubscription = this.transfersService
        .getTransfersPage({
          pageIndex: this.currentPageIndex,
          pageSize: 10,
          textSearch: this.filter,
          sortField: this.currentSortField,
          sortDirection: this.currentSortDirection,
        })
        .subscribe(
          (resp) => {
            if (_this.activePolling) {
              _this.fullTransfers = resp.pageContent;
              let cloned: ErisTableDataSource = { ...this.transfersDataSource };
              cloned.data = resp.pageContent;
              cloned.serverSideConfiguration.totalCount = resp.transfersCount;
              this.transfersDataSource = cloned;

              console.log("Transfers page load success");
              _this.startRefreshPolling();
            }
            this.spinnerService.hide();
          },
          () => {
            if (!_this.fullTransfers || !!_this.refreshTimerSubscription)
              console.log("Transfers page load error");
            this.loadTransfers(false);
          }
        );
    } else {
      this.loadTransfersSubscription = this.audiencesService
        .getAudience(this.audience.id)
        .subscribe(
          (resp) => {
            if (_this.activePolling) {
              _this.fullTransfers = resp.transfers;
              _this.filterTransfers();
              console.log("Transfers load success");
              _this.startRefreshPolling();
            }
            this.spinnerService.hide();
          },
          () => {
            if (!_this.fullTransfers || !!_this.refreshTimerSubscription)
              console.log("Transfers load error");
            this.loadTransfers(false);
          }
        );
    }
  };

  createTransfer = (): void => {
    this.stopRefreshPolling();

    let _this: TransfersBlockComponent = this;
    this.transferParametersDialogService.show(
      this.audience,
      null,
      (transfer: Transfer) => {
        _this.spinnerService.show();
        _this.transfersService.createTransfer(transfer).subscribe(
          () => {
            _this.spinnerService.hide();
            _this.dialogService.showMessage("Transfer", "Transfer created successfully");
            this.onTransferAdded.emit(this.audience);
            _this.startRefreshPolling();
          },
          () => {
            _this.spinnerService.hide();
            _this.startRefreshPolling();
          }
        );
      },
      () => {}
    );
  };

  updateTransfer = (transfer: Transfer): void => {
    this.stopRefreshPolling();

    let _this: TransfersBlockComponent = this;
    this.transferParametersDialogService.show(
      this.audience,
      transfer,
      (transfer: Transfer) => {
        _this.spinnerService.show();
        _this.transfersService.updateTransfer(transfer).subscribe(
          () => {
            _this.spinnerService.hide();
            _this.dialogService.showMessage("Transfer", "Transfer updated successfully");
            this.onTransferChanged.emit(this.audience);
            _this.startRefreshPolling();
          },
          () => {
            _this.spinnerService.hide();
            _this.startRefreshPolling();
          }
        );
      },
      () => {}
    );
  };

  createAndSendFile = (transfer: Transfer): void => {
    let _this: TransfersBlockComponent = this;
    this.dialogService.showConfirmation(
      `Are you sure to create and send file for transfer "${transfer.name}"?`,
      () => {
        _this.spinnerService.show();
        _this.transfersService.sendTransfer(transfer).subscribe(
          () => {
            _this.spinnerService.hide();
            _this.onTransferChanged.emit(this.audience);
            _this.dialogService.showMessage(
              "Transfer file",
              "Transfer file created and sent successfully"
            );
          },
          () => {
            _this.spinnerService.hide();
          }
        );
      },
      () => {}
    );
  };

  createFile = (transfer: Transfer): void => {
    let _this: TransfersBlockComponent = this;
    this.dialogService.showConfirmation(
      `Are you sure to create file for transfer "${transfer.name}"?`,
      () => {
        _this.spinnerService.show();
        _this.transfersService.createTransferFile(transfer).subscribe(
          () => {
            _this.spinnerService.hide();
            _this.onTransferChanged.emit(this.audience);
            _this.dialogService.showMessage("Transfer file", "Transfer file created successfully");
          },
          () => {
            _this.spinnerService.hide();
          }
        );
      },
      () => {}
    );
  };

  downloadFile = (transfer: Transfer, file: TransferFile): void => {
    if (file.bytesSize > environment.maxFileSizeBytesAllowed) {
      this.dialogService.showError(
        "The file size is bigger than the max size allowed to download. Contact the portal team to retrieve the file."
      );
      return;
    }

    this.stopRefreshPolling();
    this.spinnerService.show();
    this.transfersService
      .downloadTransferFile(transfer.audienceId, transfer.id, file.id)
      .subscribe(
        (result: Blob) => {
          let fileName: string;
          let targetPlatforms: any = TargetPlatforms;
          switch (transfer.targetPlatform) {
            case TargetPlatforms.WAMProxy:
              let customSegmentId: string = transfer.customAttributes.find(
                (x) => x.attributeId == TransfersAttributes.CustomSegmentId
              )?.value;
              fileName = `8415_${transfer.audience.name.replace(/[\/|\\:*?"<>]/g, " ")}_CS_${customSegmentId.replace(
                /[\/|\\:*?"<>]/g,
                " "
              )}.zip`;
              break;
            case TargetPlatforms.Xandr:
              fileName = `${targetPlatforms[transfer.targetPlatform]}_${transfer.audience.name.replace(
                /[\/|\\:*?"<>]/g,
                " "
              )}.avro`;
              break;
            case TargetPlatforms.TapTap:
              fileName = `${targetPlatforms[transfer.targetPlatform]}_${transfer.audience.name.replace(
                /[\/|\\:*?"<>]/g,
                " "
              )}.csv.gz`;
              break;
            case TargetPlatforms.TapTapOOH:
              fileName = `${targetPlatforms[transfer.targetPlatform]}_${transfer.audience.name.replace(
                /[\/|\\:*?"<>]/g,
                " "
              )}.csv`;
              break;
            default:
              fileName = `${targetPlatforms[transfer.targetPlatform]}_${transfer.audience.name.replace(
                /[\/|\\:*?"<>]/g,
                " "
              )}.zip`;
              break;
          }
          var a = document.createElement("a");
          document.body.appendChild(a);
          a.setAttribute("style", "display: none");
          a.href = URL.createObjectURL(result);
          a.download = fileName;

          this.spinnerService.hide();
          this.startRefreshPolling();

          a.click();
          URL.revokeObjectURL(a.href);
          a.remove();
        },
        () => {
          this.spinnerService.hide();
          this.startRefreshPolling();
        }
      );
  };

  sendNewFile = (transfer: Transfer): void => {
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to send a new file for transfer "${transfer.id}"?`,
      () => {
        this.spinnerService.show();
        this.transfersService.sendTransfer(transfer).subscribe(
          () => {
            this.spinnerService.hide();
            this.onTransferChanged.emit(this.audience);
            this.dialogService.showMessage("Transfer file", "New transfer file sent successfully");
            this.startRefreshPolling();
          },
          () => {
            this.spinnerService.hide();
            this.startRefreshPolling();
          }
        );
      },
      () => {
        this.startRefreshPolling();
      }
    );
  };

  sendFile = (transfer: Transfer, file: TransferFile): void => {
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to send file "${file.id}"?`,
      () => {
        this.spinnerService.show();
        this.transfersService
          .sendTransferFile(transfer.audienceId, transfer.id, file.id)
          .subscribe(
            () => {
              this.spinnerService.hide();
              this.onTransferChanged.emit(this.audience);
              this.dialogService.showMessage("Transfer file", "Transfer file sent successfully");
              this.startRefreshPolling();
            },
            () => {
              this.spinnerService.hide();
              this.startRefreshPolling();
            }
          );
      },
      () => {
        this.startRefreshPolling();
      }
    );
  };

  deleteFile = (transfer: Transfer, transferFile: TransferFile): void => {
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to remove file "${transferFile.id}"?`,
      () => {
        this.spinnerService.show();
        this.transfersService
          .removeTransferFile(transfer.audienceId, transfer.id, transferFile.id)
          .subscribe(
            () => {
              let fileIndex: number = transfer?.files?.findIndex((x) => x.id == transferFile.id);
              transfer?.files?.splice(fileIndex, 1);
              this.spinnerService.hide();
              this.dialogService.showMessage("Transfer file", "Transfer file deleted successfully");
              this.startRefreshPolling();
            },
            () => {
              this.spinnerService.hide();
              this.startRefreshPolling();
            }
          );
      },
      () => {
        this.startRefreshPolling();
      }
    );
  };

  deleteTransfer = (transfer: Transfer): void => {
    let _this: TransfersBlockComponent = this;
    this.stopRefreshPolling();
    this.dialogService.showConfirmation(
      `Are you sure to remove transfer "${transfer.id} - ${transfer.name}"?`,
      () => {
        this.spinnerService.show();
        this.transfersService.deleteTransfer(transfer).subscribe(
          () => {
            let transferIndex: number = _this.transfersDataSource?.data?.findIndex((x) => x.id == transfer.id);
            let cloned: ErisTableDataSource = { ..._this.transfersDataSource };
            cloned.data?.splice(transferIndex, 1);
            _this.transfersDataSource = cloned;
            _this.spinnerService.hide();
            _this.dialogService.showMessage("Transfer", "Transfer deleted successfully");
            _this.startRefreshPolling();
          },
          () => {
            _this.spinnerService.hide();
            _this.startRefreshPolling();
          }
        );
      },
      () => {
        _this.startRefreshPolling();
      }
    );
  };

  // ============================================================
  // Sharing transfer (UNIFICADO)
  // ============================================================
  sharingTransfer = (transfer: Transfer): void => {
    this.stopRefreshPolling();
    this.spinnerService.show();

    const platformId =
      (transfer as any)?.targetPlatformId ??
      (transfer as any)?.targetPlatform;

    if (!platformId) {
      this.spinnerService.hide();
      this.dialogService.showError("PlatformId is required.");
      this.startRefreshPolling();
      return;
    }

    forkJoin({
      availableItems: this.transferShareAudienceService
        .getShareAudienceForTransfer(transfer),

      sharedAudience: this.transferShareAudienceService
        .getSharedAudience(transfer, platformId)
    })
      .pipe(
        finalize(() => this.spinnerService.hide()),
        catchError((err) => {
          this.dialogService.showError(
            err?.message ?? "Error loading share items."
          );

          this.startRefreshPolling();
          return EMPTY;
        })
      )
      .subscribe(({ availableItems, sharedAudience }) => {
        const initialSelected: ShareAudience[] =
          this.getInitialSelectedItems(sharedAudience);

        this.openShareDialog(
          transfer,
          availableItems ?? [],
          initialSelected
        );
      });
  };

  /** Abre el diálogo de sharing */
  private openShareDialog(
    transfer: Transfer,
    availableItems: ShareAudience[],
    initialSelected: ShareAudience[]
  ): void {
    const _this: TransfersBlockComponent = this;

    this.transfersShareDialogService.show(
      this.audience,
      transfer,
      availableItems,
      initialSelected,
      (request: UpdateSharedAudienceRequest) => {
        console.log("✅ UPDATE SHARE AUDIENCE REQUEST:", request);

        _this.spinnerService.show();

        _this.transferShareAudienceService
          .saveShareAudience(request)
          .subscribe(
            () => {
              _this.spinnerService.hide();
              _this.dialogService.showMessage(
                "Transfer",
                "Transfer share audience saved successfully"
              );
              this.onTransferAdded.emit(this.audience);
              _this.startRefreshPolling();
            },
            (error: any) => {
              console.error("❌ PUT error:", error);
              _this.spinnerService.hide();
              const errorMessage = this.getSaveShareAudienceErrorMessage(error);

              _this.dialogService.showMessage(
                "Transfer",
                errorMessage
              );
              _this.startRefreshPolling();
            }
          );
      },
      () => {
        _this.startRefreshPolling();
      }
    );
  }

  toggleTransferFilesVisibility = (transfer: Transfer): void => {
    this.filesVisibleTransferId =
      this.filesVisibleTransferId != transfer.id ? transfer.id : undefined;
  };

  filterTransfers = (): void => {
    let cloned: ErisTableDataSource = { ...this.transfersDataSource };

    cloned.data = !!this.filter
      ? this.fullTransfers.filter((x: Transfer) => {
          return (
            x.id.toString().indexOf(this.filter.toUpperCase()) != -1 ||
            x.name?.toUpperCase()?.indexOf(this.filter.toUpperCase()) != -1 ||
            x.audience.id.toString().indexOf(this.filter.toUpperCase()) != -1 ||
            x.audience.name.toUpperCase().indexOf(this.filter.toUpperCase()) != -1
          );
        })
      : this.fullTransfers;

    this.transfersDataSource = cloned;
  };

  onMenuOpenedInternal = (): void => {
    this.stopRefreshPolling();
    if (this.onMenuOpened) {
      this.onMenuOpened();
    }
  };

  onMenuClosedInternal = (reason: MenuCloseReason): void => {
    if (!reason) this.startRefreshPolling();

    if (this.onMenuClosed) {
      this.onMenuClosed(reason);
    }
  };

  stopRefreshPolling = (): void => {
    if (!!this.loadTransfersSubscription) this.loadTransfersSubscription.unsubscribe();
    if (!!this.refreshTimerSubscription) this.refreshTimerSubscription.unsubscribe();

    this.activePolling = false;
    console.debug("stopRefreshPolling executed");
  };

  startRefreshPolling = (): void => {
    this.stopRefreshPolling();
    this.activePolling = true;
    this.refreshTimerSubscription = timer(2000).subscribe(() => this.loadTransfers());
    console.debug("startRefreshPolling executed");
  };

  public isWamTransfer(transfer: Transfer): boolean {
    return +transfer?.targetPlatform === TargetPlatforms.WAMProxy;
  }

  private getSaveShareAudienceErrorMessage(error: any): string {
    if (!error) {
      return "An unexpected error occurred while saving transfer share audience.";
    }

    if (typeof error?.error === "string" && error.error.trim()) {
      return error.error;
    }

    if (typeof error?.error?.message === "string" && error.error.message.trim()) {
      return error.error.message;
    }

    if (typeof error?.error?.exception?.message === "string" && error.error.exception.message.trim()) {
      return error.error.exception.message;
    }

    if (Array.isArray(error?.error?.errors) && error.error.errors.length > 0) {
      return error.error.errors.join("\n");
    }

    if (typeof error?.message === "string" && error.message.trim()) {
      return error.message;
    }

    return "An unexpected error occurred while saving transfer share audience.";
  }

  private getInitialSelectedItems(
    sharedAudience: TransferShareAudience
  ): ShareAudience[] {
    const selectedItems: ShareAudience[] = [];

    for (const platform of sharedAudience?.platforms ?? []) {
      for (const business of platform.businesses ?? []) {
        this.addBusinessSelectedItems(selectedItems, business);
      }

      for (const group of platform.groups ?? []) {
        for (const business of group.businesses ?? []) {
          this.addBusinessSelectedItems(selectedItems, business);
        }
      }
    }

    return this.dedupeShareAudienceItems(selectedItems);
  }

  private addBusinessSelectedItems(
    selectedItems: ShareAudience[],
    business: any
  ): void {
    for (const item of business?.items ?? []) {
      if (item?.selected !== true) {
        continue;
      }

      selectedItems.push({
        id: item.id,
        name: item.name,
        type: item.type,
        parentId:
          item.parentId !== null && item.parentId !== undefined
            ? item.parentId
            : business.id,
        info: item.info,
        businessId: item.businessId ?? business.id,
        businessName: item.businessName ?? business.name,
        audienceId: item.audienceId,
        audienceName: item.audienceName, 
        isError: item.isError === true,
        errorMessage: item.errorMessage ?? ""
      });
    }
  }

  private dedupeShareAudienceItems(items: ShareAudience[]): ShareAudience[] {
    const seen = new Set<string>();

    return (items ?? []).filter((item) => {
      if (!item || item.id === null || item.id === undefined) {
        return false;
      }

      const key = `${item.type}:${String(item.id)}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
  }


  public showFileButton(action: TransferFileAction, transfer: Transfer, file: TransferFile): boolean {
    switch (action) {
        case TransferFileAction.Download:
            return this.transfersService.isTransferFileActionAllowed(TransferFileAction.Download, transfer, file);

        case TransferFileAction.CreateAndSend:
            return this.transfersService.isTransferFileActionAllowed(TransferFileAction.Send, transfer, file);

        case TransferFileAction.Delete:
            return this.transfersService.isTransferFileActionAllowed(TransferFileAction.Delete ,transfer, file);
        default:
          return false;
    }
  }

  public getSharedAccounts(transfer: Transfer): any[] {
    const value = transfer.customAttributes?.find(
        attribute => attribute.attributeId === TransfersAttributes.AudienceSharedAccounts
    )?.value;

    if (!value) return [];

    try {
        const accounts = JSON.parse(value);
        return Array.isArray(accounts) ? accounts : [];
    } catch {
        return [];
    }
  }

  public getVisibleTransferAttributes(transfer: Transfer): Transfer["customAttributes"] {
    const hasSharedAccounts =
        transfer.targetPlatform === TargetPlatforms.Amazon &&
        this.getSharedAccounts(transfer).length > 0;

    return (transfer.customAttributes ?? [])
      .filter(attribute =>
          attribute.attributeId !== TransfersAttributes.AudienceSharedAccounts &&
          !(hasSharedAccounts &&
            attribute.attributeId === TransfersAttributes.AudienceExternalIdentifier)
      )
      .sort((a, b) => a.attributeId - b.attributeId);
  }

  public getSharedAudienceInfo(transfer: Transfer): { text: string; isLink: boolean } | null {
    if (transfer.targetPlatform !== TargetPlatforms.Amazon) return null;

    const accounts = this.getSharedAccounts(transfer);
    if (!accounts.length) return null;

    const isLink = accounts.length > 1;

    const attributeName = this.transfersService.getTransferAttributeDescription(
        TransfersAttributes.AudienceExternalIdentifier,
        transfer.targetPlatform
    );

    return {
        text: isLink
            ? `Shared with multiple accounts (${accounts.length})`
            : `${attributeName} : ${accounts[0].audienceId || "-"}`,
        isLink
    };
  }

  public onSharedAudienceClick(event: MouseEvent, transfer: Transfer, isLink: boolean): void {
    if (!isLink) return;

    event.preventDefault();
    event.stopPropagation();

    this.sharingTransfer(transfer);
  }
}
